import React, { useEffect, useRef, useState } from "react";
import { FaInfoCircle, FaMapMarkerAlt, FaUserClock } from "react-icons/fa";

import {
  FIELD_APP_TIMEZONE_MODES,
  formatFieldAppDateTime,
  getTimezoneModePlaceLabel,
} from "./fieldAppTimezoneDisplay";

import "./FieldAppTimezoneToggle.css";

function SelectedClock({ mode }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="field-app-timezone-toggle-clock">
      {formatFieldAppDateTime(now, mode, "hms")}
      <span className="field-app-timezone-toggle-place">
        {getTimezoneModePlaceLabel(mode)}
      </span>
    </span>
  );
}

export default function FieldAppTimezoneToggle({
  mode,
  onModeChange,
  t,
  showClock = false,
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!helpOpen) return undefined;

    const handleDocumentClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setHelpOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [helpOpen]);

  const viewerTz = getTimezoneModePlaceLabel(FIELD_APP_TIMEZONE_MODES.VIEWER);
  const dataTz = getTimezoneModePlaceLabel(FIELD_APP_TIMEZONE_MODES.DATA);

  return (
    <div className="field-app-timezone-toggle" ref={rootRef}>
      <span className="field-app-timezone-toggle-label">
        {t("Ver horários como")}:
      </span>
      <div
        className="field-app-timezone-toggle-buttons"
        role="group"
        aria-label={t("Fuso horário de exibição")}
      >
        <button
          type="button"
          className={`field-app-timezone-toggle-btn${
            mode === FIELD_APP_TIMEZONE_MODES.VIEWER ? " is-active" : ""
          }`}
          title={t("Seu horário — {{tz}}", {
            tz: viewerTz,
          })}
          aria-pressed={mode === FIELD_APP_TIMEZONE_MODES.VIEWER}
          onClick={() => onModeChange(FIELD_APP_TIMEZONE_MODES.VIEWER)}
        >
          <FaUserClock aria-hidden />
          <span>{t("Você")}</span>
        </button>
        <button
          type="button"
          className={`field-app-timezone-toggle-btn${
            mode === FIELD_APP_TIMEZONE_MODES.DATA ? " is-active" : ""
          }`}
          title={t("Horário da fazenda — {{tz}}", { tz: dataTz })}
          aria-pressed={mode === FIELD_APP_TIMEZONE_MODES.DATA}
          onClick={() => onModeChange(FIELD_APP_TIMEZONE_MODES.DATA)}
        >
          <FaMapMarkerAlt aria-hidden />
          <span>{t("Fazenda")}</span>
        </button>
      </div>
      <button
        type="button"
        className="field-app-timezone-toggle-info"
        aria-expanded={helpOpen}
        aria-label={t("Ajuda sobre horários")}
        onClick={() => setHelpOpen((open) => !open)}
      >
        <FaInfoCircle aria-hidden />
      </button>
      {showClock ? <SelectedClock mode={mode} /> : null}
      {helpOpen ? (
        <div
          className="field-app-timezone-toggle-help"
          role="dialog"
          aria-label={t("Ajuda — horários no rastreamento")}
        >
          <strong>{t("Como ler os horários")}</strong>
          <p>
            {t(
              "O GPS guarda um instante. Este controle só escolhe o relógio usado para escrever essa hora. O ponto no mapa continua o mesmo."
            )}
          </p>
          <ul>
            <li>
              <strong>{t("Você")}</strong> —{" "}
              {t(
                "hora deste computador ({{tz}}). É o lugar onde você está olhando agora.",
                { tz: viewerTz }
              )}
            </li>
            <li>
              <strong>{t("Fazenda")}</strong> —{" "}
              {t(
                "hora do estado cadastrado na conta ({{tz}}). É o relógio da fazenda e da máquina.",
                { tz: dataTz }
              )}
            </li>
          </ul>
          <p>
            {t(
              "Se a fazenda e este computador estão no mesmo fuso, os dois relógios mostram a mesma hora. Se a fazenda estiver em outro estado, «Fazenda» mostra a hora de lá."
            )}
          </p>
          <p>
            {t(
              "A escolha vale no painel e no rastreamento: mapa, cartão do GPS e gráfico."
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
}
